import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DatePipe } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { DashboardService } from '@app/features/dashboard/dashboard.service';
import { NavigationService } from '@app/shared/services/navigation-service.service';
import { AuthService } from '@app/core/services/auth.service';
import { FeatureFlagService } from '@app/shared/services/feature-flag.service';
import { I18nService } from '@app/core/services/i18n.service';
import { RedeemModalComponent } from '@shared/redeem-modal/redeem-modal.component';

@Component({
  selector: 'app-dashboard-v2',
  templateUrl: './dashboard-v2.component.html',
  styleUrls: ['./dashboard-v2.component.scss', '../dashboard/dashboard.component.scss'],
})
export class DashboardV2Component implements OnInit, OnDestroy {
  public currentDate: string | null | undefined;
  public data: any;
  public storeName = '';
  public codeCopied = false;
  @ViewChild('redeemModal') redeemModal!: RedeemModalComponent;

  private langSub?: Subscription;
  private copyTimer: any;

  constructor(
    public spinner: NgxSpinnerService,
    public router: Router,
    private dashboardService: DashboardService,
    public navigation: NavigationService,
    private authService: AuthService,
    private route: ActivatedRoute,
    public featureFlagService: FeatureFlagService,
    private translate: TranslateService,
    private i18n: I18nService
  ) {
    this.route.params.subscribe(params => {
      if (params['fromCreateStore']) {
        this.preventBackwardNavigation();
      }
    });
  }

  preventBackwardNavigation() {
    window.history.pushState(null, document.title, window.location.href);
    window.addEventListener('popstate', () => {
      window.history.pushState(null, document.title, window.location.href);
    });
  }

  ngOnInit(): void {
    this.spinner.show();
    this.storeName = this.resolveStoreName();
    this.setCurrentDate();
    this.langSub = this.translate.onLangChange.subscribe(() => this.setCurrentDate());
    this.getData();
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
    clearTimeout(this.copyTimer);
  }

  private setCurrentDate(): void {
    // Locale-aware so the header reads naturally in Arabic as well as English.
    this.currentDate = new DatePipe(this.i18n.getLocale() === 'ar' ? 'ar' : 'en-US')
      .transform(new Date(), 'mediumDate');
  }

  private resolveStoreName(): string {
    const store = this.authService.getStore();
    if (!store) {
      return '';
    }
    return (store['name'] as string) || (store['storeName'] as string) || '';
  }

  public get balance(): number {
    return this.data?.balances?.[0]?.balance || 0;
  }

  public get balanceCurrency(): string {
    return this.data?.balances?.[0]?.currency?.code || 'ILS';
  }

  public getData() {
    this.dashboardService.getDashboardData().subscribe(res => {
      this.data = res;
      this.spinner.hide();
    }, error => {
      console.log(error);
      this.spinner.hide();
    });
  }

  public copyPaymentCode(): void {
    const code = this.data?.paymentCode;
    if (!code || !navigator?.clipboard) {
      return;
    }
    navigator.clipboard.writeText(code).then(() => {
      this.codeCopied = true;
      clearTimeout(this.copyTimer);
      this.copyTimer = setTimeout(() => (this.codeCopied = false), 2000);
    }).catch(() => { /* clipboard blocked — the code is still readable on screen */ });
  }

  scanRedeem() {
    this.redeemModal.open();
  }

  public onRedeem(data: { code: string; amount: number }) {
    this.dashboardService.redeem(data.code, data.amount).subscribe(res => {
      this.redeemModal.handleRedeemSuccess();
    },
      error => {
        this.redeemModal.handleRedeemError();
        console.log(error);
      });
  }

  public onCreateVoucher() {
    this.router.navigate(['vouchers/-1']);
  }

  public goToTransactions() {
    this.router.navigate(['/transactions']);
  }

  public goToRedemptionPoints() {
    this.router.navigate(['/redemption-points']);
  }
}
